import { useEffect, useRef, useState } from "react"
import { Button, TextArea } from "react-aria-components";
import { HttpStatusCode } from "axios";
import { capitalizeFirstLetter as UppercaseFirstLetter } from "../../../../../helpers/StringHelper";
import { NonSensitiveDataApi } from "../../../../../modules/apis/NonSensitiveDataApi";
import LoadingButton from "../../../LoadingButton";
import { usePermissions } from "../../../../../modules/auth/Permissions/PermissionContext";
import { hasPermission } from "../../../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../../../modules/auth/Permissions/PermissionStrings";

interface ISidePanelSensitiveInfo {
    descriptor: string
    data: any,
    dataTarget: string
}

export default function SidePanelNonSensitiveInfo({ descriptor, data, dataTarget }: ISidePanelSensitiveInfo) {
    const [_data, setData] = useState<any>(undefined);
    const [_dataCached, setDataCached] = useState<any>(undefined);
    const [showLoader, setShowLoader] = useState<boolean>(false)
    const [sensitiveDataApi] = useState(new NonSensitiveDataApi());
    const [editing, setEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { permissions } = usePermissions();
    
    const canEdit = hasPermission(permissions!, PermissionTypes.write, PermissionConcerns.Edit_Non_PII_Notes);

    async function getData() {
        const timeout = setTimeout(() => setShowLoader(true), 200);
        const request = {
            id: data.id,
            entityName: UppercaseFirstLetter(data.entityName),
            dataTarget
        }
        const result = await sensitiveDataApi.getNotes(request);
        if (result?.status === HttpStatusCode.Ok) {
            setData(result.data?.data?.notes);
            setDataCached(result.data?.data?.notes);
        }
        setShowLoader(false);
        clearTimeout(timeout);
    }

    async function saveData(notes: string) {
        setSubmitting(true);
        const request = {
            id: data.id,
            entityName: UppercaseFirstLetter(data.entityName),
            dataTarget,
            notes
        }
        await sensitiveDataApi.updateNotes(request);
        setSubmitting(false);
    }

    function handleEditCancel() {
        setEditing(false)
        setData(_dataCached)
    }

    function handleEditSave() {
        setEditing(false)
        saveData(_data)
        setDataCached(_data)
    }

    useEffect(() => {
        if (data) {
            setEditing(false);
            getData();
        }
    }, [data])

    return (
        <div className="side-panel__info-box side-panel__info-box--sensitive margin__top--s">
            <div className="display--flex alignItems--center gap--xxs">
                <p className="small margin__bottom--xxxs margin__top--xxxs">{descriptor}</p>
            </div>
            {showLoader && <div className="loaders loaders__panel margin__top--xxs"></div>}
            {_data != undefined && _data != null && 
                <TextArea 
                    value={_data} 
                    onChange={(e) => setData(e.target.value)} 
                    data-testid="writeSensitive" 
                    onClick={() => canEdit && setEditing(true)} 
                    readOnly={!canEdit}
                    className={`side-panel__text-area ${editing ? "side-panel__text-area--editing" : ""} ${!canEdit ? "side-panel__text-area--readonly" : ""} margin__top--xxs`} 
                />
            }
            {canEdit && (editing || submitting) &&
                <div className="side-panel__text-area__buttons">
                    <Button className={"button button--secondary button--large"} isDisabled={submitting} onPress={handleEditCancel}>Cancel</Button>
                    <LoadingButton isDisabled={submitting} isLoading={submitting}
                        className="button button--primary button--large" onPress={handleEditSave}
                        loadingText="Saving"
                        buttonText="Save">
                            <></>
                    </LoadingButton>
                </div>
            }
        </div>
    )
}  